import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { usersApi, type CreateUserBody, type UpdateUserBody } from './api';
import { keys } from '@/lib/query/keys';
import { useListState } from '@/lib/hooks/useListState';
import { useGate } from '@/lib/rbac/Gate';
import { useApiError } from '@/lib/query/useApiError';
import { isAppError } from '@/lib/api/errors';
import { formatRelative } from '@/lib/format';
import { PasswordStrength } from '@/features/auth/PasswordStrength';
import { DataView, ForbiddenBanner } from '@/lib/ui/states';
import { DataTable, type Column } from '@/lib/ui/Table';
import { Pagination } from '@/lib/ui/Pagination';
import { PageHeader, MonoValue } from '@/lib/ui/Typography';
import { Tag } from '@/lib/ui/Tag';
import { Button } from '@/lib/ui/Button';
import { Dialog } from '@/lib/ui/Dialog';
import { ConfirmDialog } from '@/lib/ui/ConfirmDialog';
import { TextInput } from '@/lib/ui/Field';
import { Select } from '@/lib/ui/Select';
import { Switch } from '@/lib/ui/Switch';
import { useToast } from '@/lib/ui';
import type { Locale, Role, User } from '@/lib/api/types';

const ROLE_VALUES: Role[] = ['admin', 'editor', 'author', 'translator', 'sales', 'viewer'];
const LOCALE_VALUES: Locale[] = ['en', 'ar'];

type TFn = ReturnType<typeof useTranslation>['t'];

function roleOptions(t: TFn): { value: Role; label: string }[] {
  return ROLE_VALUES.map((value) => ({ value, label: t(`users.roles.${value}`) }));
}

function localeOptions(t: TFn): { value: Locale; label: string }[] {
  return LOCALE_VALUES.map((value) => ({ value, label: t(`users.locales.${value}`) }));
}

/** True when a validation_error carries the last-admin guard field code (spec §15.1). */
function isLastAdminError(error: unknown): boolean {
  return (
    isAppError(error) &&
    error.is('validation_error') &&
    (error.details ?? []).some((d) => d.code === 'last_admin')
  );
}

/** Users administration — admin only, gated by users:manage (spec §15.1). */
export function UsersPage() {
  const { t } = useTranslation();
  const { allowed } = useGate('users:manage');
  const qc = useQueryClient();
  const onError = useApiError();

  const list = useListState();
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<User | null>(null);

  const query = useQuery({
    queryKey: keys.users.list(list.params),
    queryFn: () => usersApi.list(list.params),
    placeholderData: (prev) => prev,
    enabled: allowed,
  });

  if (!allowed) {
    return (
      <div>
        <PageHeader eyebrow={t('users.eyebrow')} title={t('users.title')} />
        <ForbiddenBanner message={t('users.forbidden')} />
      </div>
    );
  }

  const columns: Column<User>[] = [
    { id: 'email', header: t('users.columns.email'), cell: (r) => <MonoValue className="text-ink-mid">{r.email}</MonoValue> },
    {
      id: 'name',
      header: t('users.columns.name'),
      cell: (r) => (
        <span className="flex items-center gap-xs">
          <span className="font-medium text-ink-deep">{r.name}</span>
          {r.is_service_account && <Tag variant="amber">{t('users.serviceAccount')}</Tag>}
        </span>
      ),
    },
    { id: 'role', header: t('users.columns.role'), cell: (r) => <Tag variant="neutral">{t(`users.roles.${r.role}`)}</Tag> },
    { id: 'locale', header: t('users.columns.locale'), cell: (r) => <span className="text-ink-mid uppercase">{r.locale}</span> },
    {
      id: 'is_active',
      header: t('users.columns.status'),
      cell: (r) =>
        r.is_active ? <Tag variant="teal">{t('users.status.active')}</Tag> : <Tag variant="neutral">{t('users.status.inactive')}</Tag>,
    },
    {
      id: 'last_login_at',
      header: t('users.columns.lastLogin'),
      align: 'end',
      cell: (r) => <span className="text-ink-mute">{formatRelative(r.last_login_at)}</span>,
    },
    {
      id: 'mfa_enabled',
      header: t('users.columns.mfa'),
      cell: (r) =>
        r.is_service_account ? (
          <span className="text-ink-mute" title={t('users.serviceAccountNote')}>
            —
          </span>
        ) : r.mfa_enabled ? (
          <Tag variant="teal">{t('users.mfa.enabled')}</Tag>
        ) : (
          <span className="text-ink-mute">—</span>
        ),
    },
  ];

  return (
    <div>
      <PageHeader
        eyebrow={t('users.eyebrow')}
        title={t('users.title')}
        count={query.data?.meta.pagination.total}
        actions={<Button onClick={() => setCreating(true)}>{t('users.newUser')}</Button>}
      />

      <DataView query={query} isEmpty={(d) => d.data.length === 0}>
        {(d) => (
          <>
            <DataTable
              columns={columns}
              rows={d.data}
              rowKey={(r) => r.id}
              onRowClick={(r) => setEditing(r)}
            />
            {d.meta?.pagination && (
              <Pagination
                meta={d.meta.pagination}
                onPageChange={list.setPage}
                onPerPageChange={list.setPerPage}
              />
            )}
          </>
        )}
      </DataView>

      {creating && (
        <CreateUserDialog
          onClose={() => setCreating(false)}
          onCreated={() => {
            void qc.invalidateQueries({ queryKey: keys.users.all() });
            setCreating(false);
          }}
        />
      )}

      {editing && (
        <EditUserDialog
          user={editing}
          onClose={() => setEditing(null)}
          onChanged={() => {
            void qc.invalidateQueries({ queryKey: keys.users.all() });
          }}
          onDeleted={() => {
            void qc.invalidateQueries({ queryKey: keys.users.all() });
            setEditing(null);
          }}
          onError={onError}
        />
      )}
    </div>
  );
}

// ─── Create ────────────────────────────────────────────────────────────────
function CreateUserDialog({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const { t } = useTranslation();
  const onError = useApiError();
  const toast = useToast();

  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState<Role>('viewer');
  const [locale, setLocale] = useState<Locale>('en');
  const [password, setPassword] = useState('');
  const [emailError, setEmailError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: (v: CreateUserBody) => usersApi.create(v),
    onSuccess: (u) => {
      toast.success(t('users.toasts.created', { email: u.email }));
      onCreated();
    },
    onError: (e) => {
      // 409 conflict → email already in use; surface inline.
      if (isAppError(e) && e.is('conflict')) {
        setEmailError(e.message || t('users.errors.emailInUse'));
        return;
      }
      onError(e, {
        onValidation: (fields: Record<string, string>) => setEmailError(fields.email ?? null),
      });
    },
  });

  const canSubmit = !!email.trim() && !!name.trim() && password.length >= 10;

  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      title={t('users.create.title')}
      description={t('users.create.description')}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            loading={create.isPending}
            disabled={!canSubmit}
            onClick={() => {
              setEmailError(null);
              create.mutate({ email: email.trim(), name: name.trim(), role, locale, password });
            }}
          >
            {t('users.create.submit')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-md">
        <TextInput
          type="email"
          label={t('users.fields.email')}
          required
          autoComplete="off"
          value={email}
          error={emailError}
          onChange={(e) => {
            setEmail(e.target.value);
            if (emailError) setEmailError(null);
          }}
        />
        <TextInput
          label={t('users.fields.name')}
          required
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <Select
          label={t('users.fields.role')}
          value={role}
          onValueChange={(v) => setRole(v as Role)}
          options={roleOptions(t)}
        />
        <Select
          label={t('users.fields.locale')}
          value={locale}
          onValueChange={(v) => setLocale(v as Locale)}
          options={localeOptions(t)}
        />
        <div className="flex flex-col gap-[6px]">
          <TextInput
            type="password"
            label={t('users.fields.initialPassword')}
            required
            autoComplete="new-password"
            hint={t('users.hints.minChars')}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <PasswordStrength value={password} />
        </div>
      </div>
    </Dialog>
  );
}

// ─── Edit ──────────────────────────────────────────────────────────────────
function EditUserDialog({
  user,
  onClose,
  onChanged,
  onDeleted,
  onError,
}: {
  user: User;
  onClose: () => void;
  onChanged: () => void;
  onDeleted: () => void;
  onError: ReturnType<typeof useApiError>;
}) {
  const { t } = useTranslation();
  const toast = useToast();

  const [name, setName] = useState(user.name);
  const [role, setRole] = useState<Role>(user.role);
  const [locale, setLocale] = useState<Locale>(user.locale);
  const [isActive, setIsActive] = useState(user.is_active);
  const [password, setPassword] = useState('');
  const [guardError, setGuardError] = useState<string | null>(null);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  const update = useMutation({
    mutationFn: (v: UpdateUserBody) => usersApi.update(user.id, v),
    onSuccess: (u) => {
      toast.success(t('users.toasts.updated', { email: u.email }));
      onChanged();
      onClose();
    },
    onError: (e) => {
      if (isLastAdminError(e)) {
        setGuardError(t('users.errors.lastAdmin'));
        return;
      }
      onError(e);
    },
  });

  const remove = useMutation({
    mutationFn: () => usersApi.remove(user.id),
    onSuccess: () => {
      toast.success(t('users.toasts.deleted', { email: user.email }));
      onDeleted();
    },
    onError: (e) => {
      if (isLastAdminError(e)) {
        setGuardError(t('users.errors.lastAdmin'));
        setConfirmingDelete(false);
        return;
      }
      onError(e);
      setConfirmingDelete(false);
    },
  });

  function save() {
    setGuardError(null);
    const body: UpdateUserBody = {};
    if (name.trim() !== user.name) body.name = name.trim();
    if (role !== user.role) body.role = role;
    if (locale !== user.locale) body.locale = locale;
    if (isActive !== user.is_active) body.is_active = isActive;
    if (password) body.password = password;
    update.mutate(body);
  }

  return (
    <>
      <Dialog
        open
        onOpenChange={(o) => !o && onClose()}
        title={t('users.edit.title', { name: user.name })}
        description={<MonoValue className="text-ink-mute">{user.email}</MonoValue>}
        footer={
          <>
            <Button
              variant="danger"
              onClick={() => {
                setGuardError(null);
                setConfirmingDelete(true);
              }}
            >
              {t('common.delete')}
            </Button>
            <div className="flex-1" />
            <Button variant="ghost" onClick={onClose}>
              {t('common.cancel')}
            </Button>
            <Button loading={update.isPending} onClick={save}>
              {t('users.edit.save')}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-md">
          {guardError && (
            <p
              role="alert"
              className="rounded-sm border-[0.5px] border-signal-error/40 bg-signal-error-fill px-md py-sm text-small text-signal-error"
            >
              {guardError}
            </p>
          )}

          <TextInput label={t('users.fields.name')} value={name} onChange={(e) => setName(e.target.value)} />
          <Select
            label={t('users.fields.role')}
            value={role}
            onValueChange={(v) => setRole(v as Role)}
            options={roleOptions(t)}
          />
          <Select
            label={t('users.fields.locale')}
            value={locale}
            onValueChange={(v) => setLocale(v as Locale)}
            options={localeOptions(t)}
          />
          <Switch
            label={t('users.fields.active')}
            checked={isActive}
            onCheckedChange={setIsActive}
            hint={t('users.hints.deactivate')}
          />

          {/* A service account has no usable password by construction — offering a
              reset would imply it could sign in, which is exactly what the backend
              refuses. Its credential is its API token. */}
          {user.is_service_account ? (
            <p className="rounded-sm border-[0.5px] border-hairline bg-canvas-sand px-md py-sm text-small text-ink-mid">
              {t('users.serviceAccountNote')}
            </p>
          ) : (
            <>
              <div className="flex flex-col gap-[6px] border-t-[0.5px] border-hairline pt-md">
                <TextInput
                  type="password"
                  label={t('users.fields.resetPassword')}
                  autoComplete="new-password"
                  placeholder={t('users.fields.resetPasswordPlaceholder')}
                  hint={t('users.hints.resetPassword')}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                <PasswordStrength value={password} />
              </div>

              <p className="text-small text-ink-mute">{t('users.sessionNote')}</p>
            </>
          )}
        </div>
      </Dialog>

      <ConfirmDialog
        open={confirmingDelete}
        onOpenChange={setConfirmingDelete}
        title={t('users.delete.title', { name: user.name })}
        description={t('users.delete.description', { email: user.email })}
        confirmLabel={t('users.delete.confirm')}
        tone="danger"
        loading={remove.isPending}
        onConfirm={() => remove.mutate(undefined)}
      />
    </>
  );
}
